import { css } from '@emotion/css';
import { Button, Spin, Typography, theme } from 'antd';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';

const readViewport = () => {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  return {
    width: vv?.width ?? window.innerWidth,
    height: vv?.height ?? window.innerHeight,
    left: vv?.offsetLeft ?? 0,
    top: vv?.offsetTop ?? 0,
  };
};

// Some UIs pin the layout to a fixed viewport (meta width=1024), so a width:100%
// overlay centers content off-screen on phones. Track the visual viewport and pin the
// overlay to the visible area instead.
const useVisualViewport = () => {
  const [box, setBox] = useState(readViewport);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) {
      return;
    }
    const update = () => setBox(readViewport());
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return box;
};

// signinRetry={false} once the user is signed in or a link login is under way, where a plain sign-in would be wrong
export const AuthLoadingScreen: React.FC<{ signinRetry?: boolean }> = ({ signinRetry = true }) => {
  const auth = useAuth();
  const { token } = theme.useToken();
  const [showRetry, setShowRetry] = useState(false);
  const viewport = useVisualViewport();

  // If auth is still loading after a short delay, offer a manual retry. In normal
  // environments the page has already navigated to Keycloak (unmounting this) before
  // the timer fires; it only surfaces when the redirect got stuck — e.g. an iOS
  // standalone PWA that opened Keycloak in a separate in-app browser sheet.
  useEffect(() => {
    if (!signinRetry) {
      return;
    }
    const timer = setTimeout(() => setShowRetry(true), 4000);
    return () => clearTimeout(timer);
  }, [signinRetry]);

  const containerStyle = css`
    position: fixed;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${token.marginLG}px;
  `;

  const retryStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${token.margin}px;
  `;

  return (
    <div className={containerStyle} style={{ left: viewport.left, top: viewport.top, width: viewport.width, height: viewport.height }}>
      <Spin size="large" />
      {showRetry && (
        <div className={retryStyle}>
          <Typography.Text type="secondary">Anmeldung wird geöffnet …</Typography.Text>
          <Button type="primary" onClick={() => auth.signinRedirect()}>
            Anmelden
          </Button>
        </div>
      )}
    </div>
  );
};
